import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Skeleton,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  ListItemButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Lock, MoreVertical, Pencil, Plus, Search, ShieldCheck, Trash2, Users, X } from 'lucide-react';
import type { RoleResponse } from '../../types/role';

type Filter = 'all' | 'system' | 'custom';

interface RoleListProps {
  roles: RoleResponse[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  selectedId: string | null;
  onSelect: (role: RoleResponse) => void;
  onCreate: () => void;
  onEdit: (role: RoleResponse) => void;
  onDelete: (role: RoleResponse) => void;
}

export function RoleList({
  roles,
  isLoading,
  isError,
  onRetry,
  selectedId,
  onSelect,
  onCreate,
  onEdit,
  onDelete,
}: RoleListProps) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [menu, setMenu] = useState<{ anchor: HTMLElement; role: RoleResponse } | null>(null);

  const counts = useMemo(() => {
    const system = (roles ?? []).filter((r) => r.isGlobal).length;
    return { all: roles?.length ?? 0, system, custom: (roles?.length ?? 0) - system };
  }, [roles]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (roles ?? []).filter((r) => {
      if (filter === 'system' && !r.isGlobal) return false;
      if (filter === 'custom' && r.isGlobal) return false;
      return !q || r.name.toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q);
    });
  }, [roles, search, filter]);

  const sections = [
    { key: 'custom', title: 'Custom roles', items: filtered.filter((r) => !r.isGlobal) },
    { key: 'system', title: 'System roles', items: filtered.filter((r) => r.isGlobal) },
  ].filter((s) => s.items.length > 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ px: 2, pt: 2, pb: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component="h1" variant="h5" sx={{ flex: 1, fontWeight: 700 }}>
          Roles
        </Typography>
        <Button size="small" variant="contained" startIcon={<Plus size={14} />} onClick={onCreate}>
          New role
        </Button>
      </Box>

      <Box sx={{ px: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          placeholder="Search roles…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          fullWidth
          slotProps={{
            htmlInput: { 'aria-label': 'Search roles' },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={14} aria-hidden />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={() => setSearch('')}>
                    <X size={13} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          fullWidth
          value={filter}
          onChange={(_, value: Filter | null) => value && setFilter(value)}
          aria-label="Filter roles by type"
          sx={{ '& .MuiToggleButton-root': { py: 0.5, fontSize: '0.75rem', textTransform: 'none' } }}
        >
          <ToggleButton value="all">All ({counts.all})</ToggleButton>
          <ToggleButton value="custom">Custom ({counts.custom})</ToggleButton>
          <ToggleButton value="system">System ({counts.system})</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1, pb: 1.5 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 0.5 }}>
            {[...Array(7)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={64} />
            ))}
          </Box>
        ) : isError ? (
          <Box sx={{ px: 1 }}>
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={onRetry}>Retry</Button>}>
              Could not load roles.
            </Alert>
          </Box>
        ) : sections.length === 0 ? (
          <Box sx={{ py: 5, px: 2, textAlign: 'center', color: 'text.secondary' }}>
            <ShieldCheck size={28} strokeWidth={1.4} aria-hidden />
            <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
              {search ? `No roles match “${search}”` : filter === 'custom' ? 'No custom roles yet' : 'No roles found'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {search ? 'Check the spelling or clear the search.' : 'Create a role to give a group of users the same access.'}
            </Typography>
            {search ? (
              <Button size="small" variant="outlined" onClick={() => setSearch('')}>Clear search</Button>
            ) : (
              <Button size="small" variant="outlined" startIcon={<Plus size={13} />} onClick={onCreate}>
                New role
              </Button>
            )}
          </Box>
        ) : (
          sections.map((section) => (
            <Box key={section.key} component="section" sx={{ mb: 1 }}>
              <Typography variant="overline" sx={{ display: 'block', px: 1.5, py: 0.75, color: 'text.secondary' }}>
                {section.title} · {section.items.length}
              </Typography>
              {section.items.map((role) => {
                const selected = role.id === selectedId;
                return (
                  <Box key={role.id} sx={{ position: 'relative', mb: 0.5 }}>
                    <ListItemButton
                      selected={selected}
                      onClick={() => onSelect(role)}
                      aria-current={selected ? 'true' : undefined}
                      sx={{
                        borderRadius: 1.5,
                        alignItems: 'flex-start',
                        // leave room for the actions button
                        pr: 6,
                        py: 1,
                        border: 1,
                        borderColor: selected ? 'primary.main' : 'transparent',
                        '&.Mui-selected': { bgcolor: (t) => alpha(t.palette.primary.main, 0.12) },
                        '&.Mui-selected:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.16) },
                        transition: 'background-color 150ms ease, border-color 150ms ease',
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }} noWrap>
                            {role.name}
                          </Typography>
                          {role.isGlobal && <Lock size={12} aria-label="System role" style={{ flexShrink: 0, opacity: 0.7 }} />}
                        </Box>
                        {role.description && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                            title={role.description}
                          >
                            {role.description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, color: 'text.secondary' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ShieldCheck size={12} aria-hidden />
                            <Typography variant="caption">{role.permissionCount} permissions</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Users size={12} aria-hidden />
                            <Typography variant="caption">{role.userCount}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </ListItemButton>
                    {!role.isGlobal && (
                      <IconButton
                        size="small"
                        aria-label={`Actions for ${role.name}`}
                        aria-haspopup="menu"
                        onClick={(e) => setMenu({ anchor: e.currentTarget, role })}
                        sx={{ position: 'absolute', top: 6, right: 6 }}
                      >
                        <MoreVertical size={16} />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))
        )}
      </Box>

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            if (menu) onEdit(menu.role);
            setMenu(null);
          }}
        >
          <ListItemIcon><Pencil size={15} /></ListItemIcon>
          <ListItemText>Edit name &amp; description</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) onDelete(menu.role);
            setMenu(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}><Trash2 size={15} /></ListItemIcon>
          <ListItemText>Delete role</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
